import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormError } from 'components';
import {
  Button,
  Form,
  TextInput,
  ClickableTile,
  Dropdown,
  TextArea,
  FormGroup,
  RadioButtonGroup,
  RadioButton,
  Link
} from 'carbon-components-react';
import { ChevronRight16 } from '@carbon/icons-react';
import {
  contactSupportFlag,
  contactSupportStart,
  updateProfileUpdatingFlag
} from 'redux/user/actions';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, selectSupportReqSubmitting } from 'redux/user/selector';
import { useFormik } from 'formik';
import Utils from 'utilities/helper.util';
import { USERSROLE } from 'types';
import AppConst from 'appConstants';
import { getRemedyForceSupportSchema } from './form.validation.schema';
import ButtonContent from 'components/ButtonContent';
import { setHeaderPanelExpand } from 'redux/app/actions';
import { phoneNumberFormatter } from 'utilities/validation';
import ReCAPTCHA from 'react-google-recaptcha';

interface FormValues {
  summary?: string;
  description?: string;
  category?: string;
  appAccessible?: string;
  dataCorrupted?: string;
  dbydReferrals?: string;
  name: string;
  email: string;
  phoneNumber: string;
  recaptcha?: string;
}

const supportCategoryList = [
  {
    label: 'Choose an option',
    value: ''
  },
  {
    label: 'Access',
    value: 'Access'
  },
  {
    label: 'Report damages',
    value: 'Report damages'
  },
  {
    label: 'General application issue',
    value: 'General application issue'
  },
  {
    label: 'Esri product enquiry',
    value: 'Esri product enquiry'
  },
  {
    label: 'Data',
    value: 'Data'
  },
  {
    label: 'Reporting and querying',
    value: 'Reporting and querying'
  },
  {
    label: 'Enquiry management',
    value: 'Enquiry management'
  },
  {
    label: 'Enquiry lodgement',
    value: 'Enquiry lodgement'
  },
  {
    label: 'Permits',
    value: 'Permits'
  },
  {
    label: 'Developer tools',
    value: 'Developer tools'
  },
  {
    label: 'Smartphone apps',
    value: 'Smartphone apps'
  }
];

const ContactSupportForm: React.FC<Props> = () => {
  const dispatch: any = useDispatch();
  const user: any = useSelector(selectUser);
  const submitting: boolean = useSelector(selectSupportReqSubmitting);
  const handleCancel = () => dispatch(contactSupportFlag(false));
  const {
    supportCategoryListAssetOwner,
    supportCategoryListEnquirer
  } = AppConst.support;
  const name = `${user?.firstName || ''}${
    user?.lastName ? ` ${user?.lastName}` : ''
  }`;
  const hasProfilePhone = Boolean(user?.phoneNumber);
  const userPhoneNumber = hasProfilePhone
    ? phoneNumberFormatter({
        phoneNumber: user.phoneNumber
      }).getNationalNumberFormat()
    : null;

  const categoryListItems: {
    value: string | null;
    label: string;
  }[] =
    user?.role === USERSROLE.AOADMIN ||
    user?.role === USERSROLE.AOVIEWER ||
    user?.role === USERSROLE.AOUSER
      ? [...supportCategoryList, ...supportCategoryListAssetOwner]
      : user?.role === USERSROLE.ENQUIRER
      ? [...supportCategoryList, ...supportCategoryListEnquirer]
      : [...supportCategoryList];

  const [isFormFieldsDirty, setIsFormFieldsDirty] = useState<boolean>(true);
  const [reCaptchaToken, setReCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  const {
    touched,
    errors,
    values,
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      name: name || '',
      email: user?.email || '',
      phoneNumber: userPhoneNumber || ''
    },
    validationSchema: getRemedyForceSupportSchema(hasProfilePhone),
    onSubmit: (values: FormValues) => {
      const phoneNumber = hasProfilePhone
        ? user.phoneNumber
        : values.phoneNumber;
      const authPayload = {
        summary: values.summary,
        description: values.description,
        category: values.category,
        isApplicationAccessible: values.appAccessible === 'yes' ? true : false,
        isDataCorrupted: values.dataCorrupted === 'yes' ? true : false,
        isResponseHandlingIssue: values.dbydReferrals === 'yes' ? true : false,
        name: values.name,
        email: values.email,
        phoneNumber
      };
      const unAuthPayload = {
        summary: values.summary,
        description: values.description,
        category: values.category,
        isApplicationAccessible: values.appAccessible === 'yes' ? true : false,
        name: values.name,
        email: values.email,
        phoneNumber: values.phoneNumber,
        recaptcha: values.recaptcha
      };

      const payload = user
        ? { role: user?.role, request: authPayload }
        : { role: null, request: unAuthPayload };
      dispatch(contactSupportStart(payload));
    }
  });

  /**
   * Handle ReCAPTCHA token change.
   *
   * WHY: When the user completes the ReCAPTCHA challenge, we need to store the token
   * in both local state and form state for submission.
   */
  const handleRecaptchaChange = (token: string | null) => {
    setReCaptchaToken(token);
    setFieldValue('recaptcha', token || '');
  };

  /**
   * Form validation effect.
   *
   * WHY: We need to track form dirtiness to enable/disable the submit button.
   * For unauthenticated users, ReCAPTCHA completion is required.
   */
  useEffect(() => {
    let isDirty = true;
    const touchedFields = Utils.isObjectEmpty(touched);
    const isAODBYDAdmin =
      user?.role === USERSROLE.AOADMIN ||
      user?.role === USERSROLE.AOVIEWER ||
      user?.role === USERSROLE.DBYDADMIN;

    if (!touchedFields) {
      if (!user) {
        isDirty =
          Object.values(values).filter((v) => v === '').length > 0 ||
          !reCaptchaToken;
      } else {
        const isValidDC = isAODBYDAdmin && values.dataCorrupted === undefined;
        isDirty =
          Object.values(values).filter((v) => v === '').length > 0 || isValidDC;
      }
      setIsFormFieldsDirty(isDirty);
    }
  }, [errors, user, reCaptchaToken, values, touched]);

  const handleOpenProfile = () => {
    dispatch(updateProfileUpdatingFlag(true));
    dispatch(setHeaderPanelExpand({ panel: 'account', isOpen: false }));
  };

  const changeHandler = (val: any, key: string) => {
    if (key === 'appAccessible') {
      setFieldValue('appAccessible', val);
    }
    if (key === 'dbydReferrals') {
      setFieldValue('dbydReferrals', val);
    }
    if (key === 'dataCorrupted') {
      setFieldValue('dataCorrupted', val);
    }
  };

  return (
    <>
      <div className="global-right-modal contact-support">
        <section className="global-right-modal--wrapper">
          <section className="global-right-modal--top">
            <ClickableTile
              href="https://help.byda.com.au/"
              target="_blank"
            >
              <h3 className="clickable-tile--header">
                Tried the self help pages before contacting support?
              </h3>
              <div className="clickable-tile--button">
                <span>Solutions to common problems</span>
                <ChevronRight16 />
              </div>
            </ClickableTile>
            <div className="global-right-modal--header">
              <h1 className="modal-form-main-heading">Contact support …</h1>
              <Button kind="ghost" size="small" onClick={() => handleCancel()}>
                Cancel
              </Button>
            </div>
          </section>
          <hr className="section--divider" />
          <Form className="modal-form--wrapper" onSubmit={handleSubmit}>
            <div className="custom-scroller">
              <section className="modal-form-item">
                <TextArea
                  cols={50}
                  id="summary"
                  labelText="Subject"
                  placeholder="Brief issue summary"
                  rows={3}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={40}
                />
                {errors.summary && touched.summary && (
                  <FormError>{errors.summary}</FormError>
                )}
              </section>
              <section className="modal-form-item">
                <TextArea
                  cols={50}
                  id="description"
                  labelText="Description"
                  placeholder="Describe the problem you are having, including as much detail as possible"
                  rows={3}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={250}
                />
                {errors.description && touched.description && (
                  <FormError>{errors.description}</FormError>
                )}
              </section>
              <section className="modal-form-item">
                <Dropdown
                  id="category"
                  titleText="Category"
                  label=""
                  items={categoryListItems}
                  itemToString={(item: any) => item.label}
                  onBlur={handleBlur}
                  initialSelectedItem={categoryListItems[0]}
                  onChange={(item: any) =>
                    setFieldValue('category', item?.selectedItem?.value)
                  }
                />
                {errors.category && touched.category && (
                  <FormError>{errors.category}</FormError>
                )}
              </section>
              <section className="modal-form-item">
                <FormGroup legendText="Can you access the application?">
                  <RadioButtonGroup
                    name="appAccessible"
                    onChange={(val: string) => {
                      changeHandler(val, 'appAccessible');
                    }}
                  >
                    <RadioButton
                      id="appAccessible-yes"
                      labelText="Yes"
                      value="yes"
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    <RadioButton
                      id="aappAccessible-no"
                      labelText="No"
                      value="no"
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </RadioButtonGroup>
                </FormGroup>
                {touched.appAccessible && errors.appAccessible && (
                  <FormError>{errors.appAccessible}</FormError>
                )}
              </section>
              {user?.role === USERSROLE.AOADMIN ||
              user?.role === USERSROLE.AOVIEWER ||
              user?.role === USERSROLE.DBYDADMIN ? (
                <section className="modal-form-item">
                  <FormGroup legendText="Can you respond to BYDA referrals?">
                    <RadioButtonGroup
                      name="dbydReferrals"
                      onChange={(val: string) =>
                        changeHandler(val, 'dbydReferrals')
                      }
                    >
                      <RadioButton
                        id="dbydReferrals-yes"
                        labelText="Yes"
                        value="yes"
                        onBlur={handleBlur}
                      />
                      <RadioButton
                        id="dbydReferrals-no"
                        labelText="No"
                        value="no"
                        onBlur={handleBlur}
                      />
                    </RadioButtonGroup>
                  </FormGroup>
                  {touched.dbydReferrals && errors.dbydReferrals && (
                    <FormError>{errors.dbydReferrals}</FormError>
                  )}
                </section>
              ) : null}{' '}
              <section className="modal-form-item">
                <FormGroup legendText="Has any data been deleted or corrupted?">
                  <RadioButtonGroup
                    name="dataCorrupted"
                    onChange={(val: string) =>
                      changeHandler(val, 'dataCorrupted')
                    }
                  >
                    <RadioButton
                      id="dataCorrupted-yes"
                      labelText="Yes"
                      value="yes"
                      onBlur={handleBlur}
                    />
                    <RadioButton
                      id="dataCorrupted-no"
                      labelText="No"
                      value="no"
                      onBlur={handleBlur}
                    />
                    <RadioButton
                      id="dataCorrupted-dontkmow"
                      labelText="Don't know"
                      value="dontknow"
                      onBlur={handleBlur}
                    />
                  </RadioButtonGroup>
                </FormGroup>
                {touched.dataCorrupted && errors.dataCorrupted && (
                  <FormError>{errors.dataCorrupted}</FormError>
                )}
              </section>
              <hr className="section--divider" />
              {user ? (
                <label className="update-profile--footer">
                  You can update contact details in{' '}
                  <Link href="#" onClick={() => handleOpenProfile()}>
                    your profile
                  </Link>
                </label>
              ) : null}
              <section className="modal-form-item">
                <TextInput
                  id="name"
                  name="name"
                  labelText="Your name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  tabIndex={user && user?.firstName !== '' ? -1 : 0}
                  readOnly={user && user?.firstName !== ''}
                  value={
                    (user && `${user?.firstName} ${user?.lastName}`) ||
                    values?.name ||
                    ''
                  }
                />
                {errors.name && touched.name && (
                  <FormError>{errors.name}</FormError>
                )}
              </section>
              <section className="modal-form-item">
                <TextInput
                  id="email"
                  name="email"
                  labelText="Your email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  tabIndex={user && user?.email !== '' ? -1 : 0}
                  readOnly={user && user?.email !== ''}
                  value={values?.email || user?.email || ''}
                />
                {errors.email && touched.email && (
                  <FormError>{errors.email}</FormError>
                )}
              </section>
              <section className="modal-form-item">
                <TextInput
                  id="phoneNumber"
                  name="phoneNumber"
                  labelText="Your best phone number"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  tabIndex={user && userPhoneNumber ? -1 : 0}
                  readOnly={Boolean(user && hasProfilePhone)}
                  value={userPhoneNumber || values?.phoneNumber || ''}
                />
                {errors.phoneNumber && touched.phoneNumber && (
                  <FormError>{errors.phoneNumber}</FormError>
                )}
              </section>
              {!user ? (
                <section className="modal-form-item">
                  <div
                    style={{
                      marginTop: '1rem',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.REACT_APP_RECAPTCHA_KEY || ''}
                      onChange={handleRecaptchaChange}
                    />
                  </div>
                  {errors.recaptcha && touched.recaptcha && (
                    <FormError>{errors.recaptcha}</FormError>
                  )}
                </section>
              ) : null}
            </div>
            <section className="modal-form-button--wrapper">
              <div className="button--set">
                <Button
                  id="cancel"
                  name="cancel"
                  kind="secondary"
                  data-testid="cancel"
                  onClick={() => handleCancel()}
                >
                  Cancel
                </Button>
                <Button
                  id="create"
                  name="create"
                  type="submit"
                  kind="primary"
                  data-testid="submit"
                  disabled={isFormFieldsDirty || !Utils.isObjectEmpty(errors)}
                >
                  <ButtonContent isLoading={submitting}>Submit</ButtonContent>
                </Button>
              </div>
            </section>
          </Form>
        </section>
      </div>
    </>
  );
};

const propTypes = {
  formError: PropTypes.string,
  setError: PropTypes.func.isRequired
};

type Props = PropTypes.InferProps<typeof propTypes>;

ContactSupportForm.defaultProps = {
  formError: '',
  setError: () => {}
};
export default ContactSupportForm;
